import React, { useState, useEffect, useCallback } from 'react';
import { FileCheck, AlertCircle, ChevronUp, ChevronDown, ExternalLink, RefreshCw } from 'lucide-react';
import api from '../utils/api';
import ReviewDetailModal from './modals/ReviewDetailModal';

// Extract DynamoDB values - moved outside component to avoid dependency issues
const extractDynamoValue = (obj) => {
    if (!obj || obj === null) return null;
    
    // Handle DynamoDB NULL type
    if (obj.NULL === true) return null;
    
    // Handle DynamoDB String type
    if (obj.S !== undefined) return obj.S;
    
    // Handle DynamoDB Number type
    if (obj.N !== undefined) {
      const num = parseFloat(obj.N);
      return isNaN(num) ? obj.N : num;
    }
    
    // Handle DynamoDB Boolean type
    if (obj.BOOL !== undefined) return obj.BOOL;
    
    // Handle DynamoDB Map type (nested object)
    if (obj.M) {
      const result = {};
      Object.keys(obj.M).forEach(key => {
        result[key] = extractDynamoValue(obj.M[key]);
      });
      return result;
    }
    
    // Handle DynamoDB List type (array)
    if (obj.L) {
      return obj.L.map(item => extractDynamoValue(item));
    }
    
    // If it's already a plain object/array, check if it needs recursive extraction
    if (typeof obj === 'object' && !Array.isArray(obj)) {
      // Check if any values are still in DynamoDB format
      const needsExtraction = Object.values(obj).some(val => 
        val && typeof val === 'object' && ('S' in val || 'M' in val || 'L' in val || 'N' in val || 'BOOL' in val || val.NULL === true)
      );
      
      if (needsExtraction) {
        const result = {};
        Object.keys(obj).forEach(key => {
          result[key] = extractDynamoValue(obj[key]);
        });
        return result;
      }
    }
    
    // Return as-is if already extracted or primitive
    return obj;
  };

const ReviewedCHG = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'review_timestamp', direction: 'desc' });
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [lastAttemptedReview, setLastAttemptedReview] = useState(null);
  const [renderError, setRenderError] = useState(null);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.fetchChgReviews().catch(err => {
        if (err.message?.includes('Unauthorized')) {
          return { reviews: [] };
        }
        throw err;
      });
      
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid API response format');
      }
      
      const rawReviews = response.reviews || [];
      
      if (!Array.isArray(rawReviews)) {
        throw new Error('API did not return reviews array');
      }
      
      const normalizedReviews = rawReviews.map(review => {
        try {
          // Extract the entire review object from DynamoDB format
          const normalized = extractDynamoValue(review) || {};
          
          // Access nested objects (they should already be extracted by extractDynamoValue)
          const changeInfo = normalized?.change_info || {};
          const fullChangeData = normalized?.full_change_data || {};
          const aiReview = normalized?.ai_review || {};
          const fullAiReview = normalized?.full_ai_review || aiReview;
          
          // Extract change_number - could be at root or nested
          const changeNumber = normalized?.change_number || normalized?.PK || normalized?.change_number?.S || null;
          
          // Extract short_description from change_info or full_change_data
          const shortDescription = changeInfo?.short_description || 
                                   fullChangeData?.short_description || 
                                   fullChangeData?.short_description?.S || 
                                   null;
          
          // Extract u_brand from full_change_data
          const uBrand = fullChangeData?.u_brand || 
                       fullChangeData?.u_brand?.S || 
                       null;
          
          // Extract compliance_status from ai_review or full_ai_review
          const complianceStatus = fullAiReview?.compliance_status || 
                                  aiReview?.compliance_status || 
                                  fullAiReview?.compliance_status?.S || 
                                  aiReview?.compliance_status?.S || 
                                  null;
          
          // Extract risk from change_info or full_change_data
          const risk = changeInfo?.risk || 
                      fullChangeData?.risk || 
                      changeInfo?.risk?.S || 
                      fullChangeData?.risk?.S || 
                      null;
          
          // Extract review_timestamp
          const reviewTimestamp = normalized?.review_timestamp || 
                                 normalized?.SK || 
                                 normalized?.review_timestamp?.S || 
                                 normalized?.SK?.S || 
                                 null;
          
          // Extract posted_to_servicenow (boolean)
          const postedToServiceNow = normalized?.posted_to_servicenow === true || 
                                     normalized?.posted_to_servicenow?.BOOL === true || 
                                     false;
          
          // Extract sys_id
          const sysId = normalized?.sys_id || 
                       fullChangeData?.sys_id || 
                       normalized?.sys_id?.S || 
                       fullChangeData?.sys_id?.S || 
                       null;
          
          return {
            change_number: changeNumber,
            short_description: shortDescription,
            u_brand: uBrand,
            compliance_status: complianceStatus,
            risk: risk,
            review_timestamp: reviewTimestamp,
            posted_to_servicenow: postedToServiceNow,
            sys_id: sysId,
            // Store the full normalized review for detail view
            _fullReview: normalized
          };
        } catch (err) {
          console.error('Error normalizing review:', err, review);
          return null;
        }
      }).filter(review => review !== null);
      
      setReviews(normalizedReviews);
    } catch (err) {
      console.error('Failed to fetch CHG reviews:', err);
      setError(err.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRowClick = async (review) => {
    if (!review || !review.change_number) {
      console.error('Invalid review object:', review);
      setDetailError('Invalid review data');
      return;
    }
    
    setDetailLoading(true);
    setDetailError(null);
    setLastAttemptedReview(review);
    try {
      const response = await api.fetchChgReviewDetail(review.change_number);
      
      // Handle DynamoDB format response
      let fullReview = null;
      if (response?.reviews && Array.isArray(response.reviews)) {
        // Extract the first review from DynamoDB format
        const rawReview = response.reviews[0];
        fullReview = extractDynamoValue(rawReview) || rawReview;
      } else if (response && typeof response === 'object') {
        // Response might be a single review in DynamoDB format
        fullReview = extractDynamoValue(response) || response;
      }
      
      // Fallback to the review from the list if detail fetch fails
      if (!fullReview && review._fullReview) {
        fullReview = review._fullReview;
      } else if (!fullReview) {
        fullReview = review;
      }
      
      setSelectedReview(fullReview);
    } catch (err) {
      console.error('Failed to fetch review details:', err);
      // If we have the full review cached, use it
      if (review._fullReview) {
        setSelectedReview(review._fullReview);
        setDetailError(null);
      } else {
        setDetailError(err.message || 'Failed to load review details');
      }
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortedReviews = React.useMemo(() => {
    try {
      return [...reviews].sort((a, b) => {
        const aVal = a?.[sortConfig.key] || '';
        const bVal = b?.[sortConfig.key] || '';
        const modifier = sortConfig.direction === 'desc' ? -1 : 1;
        return aVal < bVal ? -modifier : aVal > bVal ? modifier : 0;
      });
    } catch (err) {
      console.error('Error sorting reviews:', err);
      return reviews;
    }
  }, [reviews, sortConfig]);

  const getComplianceBadge = (status) => {
    const styles = {
      'Compliant': 'bg-green-500/20 text-green-400 border-green-500/40',
      'Partially_Compliant': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
      'Non_Compliant': 'bg-red-500/20 text-red-400 border-red-500/40'
    };
    return styles[status] || 'bg-gray-500/20 text-text-secondary border-gray-500/40';
  };

  const getRiskBadge = (level) => {
    const styles = {
      'Low': 'bg-green-500/20 text-green-400 border-green-500/40',
      'Medium': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
      'High': 'bg-red-500/20 text-red-400 border-red-500/40'
    };
    return styles[level] || 'bg-gray-500/20 text-text-secondary border-gray-500/40';
  };


  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts;
    }
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return sortConfig.direction === 'desc' ? 
      <ChevronDown size={14} className="inline ml-1" /> : 
      <ChevronUp size={14} className="inline ml-1" />;
  };

  const TableHeader = ({ column, label }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider cursor-pointer hover:text-blue-400 transition-colors"
      onClick={() => handleSort(column)}
    >
      {label}<SortIcon column={column} />
    </th>
  );

  // Error boundary for render errors
  if (renderError) {
    return (
      <div className="p-8 flex items-center justify-center min-h-96">
        <div className="flex flex-col items-center space-y-3 text-red-400">
          <AlertCircle size={48} />
          <h3 className="text-xl font-bold">Error Loading Component</h3>
          <p className="text-sm text-text-secondary">{renderError}</p>
          <button
            onClick={() => {
              setRenderError(null);
              setError(null);
              fetchReviews();
            }}
            className="mt-4 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/20 transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-96">
        <div className="flex items-center space-x-3 text-blue-400">
          <RefreshCw size={24} className="animate-spin" />
          <span className="text-lg">Loading reviews...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 relative">
      {/* Background effects */}

{/* Header */}
      <div className="mb-6 relative z-10 flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center border border-blue-500/30"
                 >
              <FileCheck size={20} className="text-blue-400"  />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-blue-400 tracking-tight">
                Reviewed CHG
              </h2>
              <p className="text-sm text-text-muted">{reviews.length} reviews found</p>
            </div>
          </div>
        </div>
        <button
          onClick={fetchReviews}
          className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-dark-border/40 border border-blue-500/40 text-blue-400 hover:bg-blue-500/10 transition-all"
        >
          <RefreshCw size={16} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start space-x-3 relative z-10">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 font-medium">Failed to load reviews</p>
            <p className="text-sm text-red-400/70">{error}</p>
          </div>
        </div>
      )}

      {/* Reviews Table */}
      <div className="bg-surface border border-border rounded-lg overflow-hidden relative z-10"
           >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-dark-border/40 border-b border-border">
              <tr>
                <TableHeader column="change_number" label="Change #" />
                <TableHeader column="short_description" label="Description" />
                <TableHeader column="u_brand" label="Brand" />
                <TableHeader column="compliance_status" label="Compliance Status" />
                <TableHeader column="risk" label="Risk" />
                <TableHeader column="review_timestamp" label="Reviewed At" />
                <TableHeader column="posted_to_servicenow" label="Posted" />
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-border/50">
              {sortedReviews.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-text-muted">
                    No reviews found
                  </td>
                </tr>
              ) : (
                sortedReviews.map((review, idx) => {
                  if (!review) return null;
                  
                  const description = review?.short_description || '-';
                  const truncatedDesc = typeof description === 'string' && description.length > 50 
                    ? description.substring(0, 50) + '...' 
                    : description;
                  
                  return (
                    <tr 
                      key={`${review?.change_number || 'unknown'}-${review?.review_timestamp || idx}-${idx}`}
                      className="hover:bg-blue-500/5 cursor-pointer transition-colors"
                      onClick={() => review && handleRowClick(review)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <span className="text-blue-400 font-medium">{review?.change_number || '-'}</span>
                          {review?.sys_id && (
                            <a 
                              href={`https://dish.service-now.com/nav_to.do?uri=change_request.do?sys_id=${review.sys_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-text-muted hover:text-blue-400 transition-colors"
                            >
                              <ExternalLink size={14} />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary">
                        {truncatedDesc}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary">
                        {review?.u_brand || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded border ${getComplianceBadge(review?.compliance_status)}`}>
                          {(review?.compliance_status || 'Unknown').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded border ${getRiskBadge(review?.risk)}`}>
                          {review?.risk || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary">
                        {formatTimestamp(review?.review_timestamp)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded border ${
                          review?.posted_to_servicenow 
                            ? 'bg-green-500/20 text-green-400 border-green-500/40' 
                            : 'bg-gray-500/20 text-text-secondary border-gray-500/40'
                        }`}>
                          {review?.posted_to_servicenow ? 'Yes' : 'No'}
                        </span>
                      </td>
                    </tr>
                  );
                }).filter(Boolean)
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedReview && (
        <ReviewDetailModal 
          review={selectedReview} 
          onClose={() => setSelectedReview(null)} 
        />
      )}

      {/* Loading Overlay for Detail Fetch */}
      {detailLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="flex flex-col items-center space-y-3 text-blue-400">
            <RefreshCw size={48} className="animate-spin" />
            <span className="text-lg font-medium">Loading review details...</span>
          </div>
        </div>
      )}

      {/* Error Modal for Detail Fetch */}
      {detailError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-surface border border-red-500/30 rounded-lg p-6 max-w-md mx-4"
               >
            <div className="flex items-start space-x-3 mb-4">
              <AlertCircle size={24} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-bold text-red-400 mb-2">Failed to Load Review Details</h3>
                <p className="text-sm text-text-primary">{detailError}</p>
              </div>
            </div>
            <div className="flex space-x-3 justify-end">
              <button
                onClick={() => setDetailError(null)}
                className="px-4 py-2 rounded bg-dark-border/40 border border-gray-500/40 text-text-primary hover:bg-dark-border/60 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setDetailError(null);
                  if (lastAttemptedReview) {
                    handleRowClick(lastAttemptedReview);
                  }
                }}
                className="px-4 py-2 rounded bg-blue-500/10 border border-blue-500/40 text-blue-400 hover:bg-blue-500/20 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewedCHG;
